/**
 * Native connectivity reload policy and connectivity-tracking orchestration
 * behind NetworkStatusBanner. Android's connectivity service emits redundant
 * `connected: true` status events while the device stays online (capability
 * changes, WiFi↔cellular handoffs, revalidation), so only a genuine
 * offline→online transition warrants a reload to recover in-flight requests
 * and stale state.
 *
 * Contains:
 * - shouldReloadOnReconnect — pure previous/next connectivity → reload decision
 * - startConnectivityTracking — wires the one-shot Network.getStatus()
 *   snapshot and the ongoing networkStatusChange listener to a status/reload
 *   callback pair, reconciling the two independent bridge round trips
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * @param {boolean} wasConnected - connectivity before the status event.
 * @param {boolean} isConnected  - connectivity reported by the status event.
 * @returns {boolean} True only for an offline→online transition; redundant
 *   online events and offline events never reload.
 */
export function shouldReloadOnReconnect(wasConnected, isConnected) {
  return !wasConnected && isConnected;
}

/**
 * @param {object} deps
 * @param {() => Promise<{connected: boolean}>} deps.getStatus - one-shot snapshot.
 * @param {(cb: (status: {connected: boolean}) => void) => Promise<{remove: () => void}>} deps.addListener
 *   - registers the ongoing status listener; resolves with its handle.
 * @param {(connected: boolean) => void} deps.onStatusChange - called with the
 *   reconciled connectivity value whenever it changes.
 * @param {() => void} deps.onReload - called instead of onStatusChange on a
 *   genuine offline→online transition.
 * @param {boolean} [deps.initialConnected] - assumed connectivity before the
 *   snapshot or any event arrives.
 * @returns {() => void} teardown — call on unmount.
 */
export function startConnectivityTracking({
  getStatus,
  addListener,
  onStatusChange,
  onReload,
  initialConnected = true,
}) {
  let connected = initialConnected;
  let hasReceivedEvent = false;
  let cancelled = false;
  let listenerHandle;

  getStatus()
    .then(status => {
      // getStatus() and the listener are independent bridge round trips with
      // no ordering guarantee. If a real event already arrived, this snapshot
      // was captured before that transition and would clobber newer state.
      if (cancelled || hasReceivedEvent) return;
      connected = status.connected;
      onStatusChange(connected);
    })
    .catch(() => {});

  addListener(status => {
    if (cancelled) return;
    hasReceivedEvent = true;
    if (shouldReloadOnReconnect(connected, status.connected)) {
      onReload();
      return;
    }
    connected = status.connected;
    onStatusChange(connected);
  })
    .then(handle => {
      // Teardown may already have run by the time this resolves; stashing
      // the handle then would leak the native listener forever.
      if (cancelled) {
        handle.remove();
      } else {
        listenerHandle = handle;
      }
    })
    .catch(() => {});

  return () => {
    cancelled = true;
    if (listenerHandle) listenerHandle.remove();
  };
}
